import { useState, useMemo, useCallback, useEffect } from 'react';

import { useLocation } from 'react-router-dom';
import { getHelpResourceForRoute, type HelpResource } from '@/config/help-videos';

const DISMISSED_KEY = 'help-widget-dismissed';
const SEEN_KEY = 'help-widget-seen';

function getStoredSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function persistSet(key: string, set: Set<string>) {
  localStorage.setItem(key, JSON.stringify([...set]));
}

export function useHelpResource() {
  const { pathname } = useLocation();

  const resource = useMemo(() => getHelpResourceForRoute(pathname), [pathname]);
  const resourceKey = resource?.video?.loomId ?? resource?.article?.url ?? null;

  const [isPermanentlyDismissed, setIsPermanentlyDismissed] = useState(() => {
    if (!resourceKey) return false;
    return getStoredSet(DISMISSED_KEY).has(resourceKey);
  });

  const [collapsed, setCollapsed] = useState(() => {
    if (!resourceKey) return false;
    return getStoredSet(SEEN_KEY).has(resourceKey);
  });

  useEffect(() => {
    if (!resourceKey) {
      setIsPermanentlyDismissed(false);
      setCollapsed(false);
      return;
    }
    setIsPermanentlyDismissed(getStoredSet(DISMISSED_KEY).has(resourceKey));
    setCollapsed(getStoredSet(SEEN_KEY).has(resourceKey));
  }, [resourceKey]);

  const collapse = useCallback(() => {
    setCollapsed(true);
    if (!resourceKey) return;
    const set = getStoredSet(SEEN_KEY);
    set.add(resourceKey);
    persistSet(SEEN_KEY, set);
  }, [resourceKey]);

  const expand = useCallback(() => {
    setCollapsed(false);
  }, []);

  const dismissPermanently = useCallback(() => {
    setIsPermanentlyDismissed(true);
    if (!resourceKey) return;
    const set = getStoredSet(DISMISSED_KEY);
    set.add(resourceKey);
    persistSet(DISMISSED_KEY, set);
  }, [resourceKey]);

  return {
    resource,
    isPermanentlyDismissed,
    collapsed,
    collapse,
    expand,
    dismissPermanently,
  };
}
